import React, { useState } from 'react';
import PropTypes from 'prop-types';
import ImageGallery from 'react-image-gallery';
import styled from 'styled-components';
import { Link, navigate } from 'gatsby';

import breakpoints from '@repo/ui-components/breakpoints';
import Button from '@repo/ui-components/button/Button';

import 'react-image-gallery/styles/css/image-gallery.css';
import dayjs from 'dayjs';
import dayjsUtc from 'dayjs/plugin/utc';
import dayjsTimezone from 'dayjs/plugin/timezone';

dayjs.extend(dayjsUtc);
dayjs.extend(dayjsTimezone);

const renderSliderImage = image => (
  <>
    <picture>
      <source
        media={`(max-width: ${breakpoints.sm})`}
        srcSet={`${image.mobileImage}?width=640`}
      />
      <source
        media={`(max-width: ${breakpoints.md})`}
        srcSet={`${image.mobileImage}?width=768`}
      />
      <source
        media={`(max-width: ${breakpoints.lg})`}
        srcSet={`${image.original}?width=1024`}
      />
      <source
        media={`(max-width: ${breakpoints.xl})`}
        srcSet={`${image.original}?width=1280`}
      />
      <ResponsiveImage
        className="image-gallery-image"
        src={image.original}
        alt={image.originalAlt}
        width="1280"
        height="482"
      />
    </picture>
    {(image?.slide_title || image?.subheading || image?.button_label) && (
      <TextOverlay
        className="image-gallery-description"
        textColor={image.textColor}
        boxColor={image.boxColor}
        boxTransparency={image.boxTransparency}
      >
        {image?.slide_title && <h1>{image.slide_title}</h1>}
        {image?.subheading && <p>{image.subheading}</p>}
        {image?.button_label && image?.link && (
          <Button onClick={() => navigate(image.link)}>
            {image.button_label}
          </Button>
        )}
      </TextOverlay>
    )}
  </>
);

const ResponsiveImage = styled.img`
  width: 100%;
  height: 100%;
`;

const hexToRgb = hex => {
  let newHex = hex;
  const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
  newHex = hex.replace(shorthandRegex, (m, r, g, b) => r + r + g + g + b + b);

  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(newHex);
  return (
    result &&
    `${parseInt(result[1], 16)},${parseInt(result[2], 16)},${parseInt(
      result[3],
      16
    )}`
  );
};

const TextOverlay = styled.span`
  display: none;

  @media screen and (min-width: ${breakpoints.lg}) {
    display: block;
  }

  && {
    background-color: ${({ boxColor, boxTransparency }) =>
      `rgba(${hexToRgb(boxColor || '#000')}, ${boxTransparency || '0.5'})`};
    color: ${({ textColor }) => textColor || '#fff'};
  }
`;

const MobileTextOverlay = styled.div`
  background-color: ${({ boxColor, boxTransparency }) =>
    `rgba(${hexToRgb(boxColor || '#000')}, ${boxTransparency || '0.5'})`};
  color: ${({ textColor }) => textColor || '#fff'};
  text-align: center;
  padding: 1rem;

  h1 {
    margin: 0;
  }

  @media screen and (min-width: ${breakpoints.lg}) {
    display: none;
  }
`;

const shouldRenderAsLink = image =>
  image?.link ? (
    <Link to={image.link}>{renderSliderImage(image)}</Link>
  ) : (
    renderSliderImage(image)
  );

const Container = styled.div`
  .image-gallery-icon {
    z-index: 1;
    min-width: 48px;
  }
  .image-gallery-slide .image-gallery-description {
    height: fit-content;

    ${({ boxVerticalAlignment, boxHorizontalAlignment }) => {
      switch (`${boxVerticalAlignment},${boxHorizontalAlignment}`) {
        case 'bottom,center':
          return 'transform: translate(-50%, 0%);';
        case 'center,left':
          return 'transform: translate(0, -50%);';
        case 'center,center':
          return 'transform: translate(-50%, -50%);';
        case 'center,right':
          return 'transform: translate(0%, -50%);';
        case 'top,center':
          return 'transform: translate(-50%, 0);';
        default:
          return '';
      }
    }}

    ${({ boxVerticalAlignment }) => {
      if (boxVerticalAlignment === 'top') {
        return 'top: 10%;';
      }
      if (boxVerticalAlignment === 'bottom') {
        return 'bottom: 10%;';
      }
      return 'top: 50%; bottom: inherit;';
    }}
    ${({ boxHorizontalAlignment }) => {
      if (boxHorizontalAlignment === 'left') {
        return 'left: 10%; right: inherit;';
      }
      if (boxHorizontalAlignment === 'right') {
        return 'right: 10%; left: inherit;';
      }
      return 'left: 50%; right: inherit;';
    }}

    .image-gallery-left-nav,
    .image-gallery-right-nav {
      padding: 1rem;
    }
  }
`;

const filterOutScheduledSlides = slides =>
  slides.filter(slide => {
    if (slide.scheduledSlideEnabled && slide.startDate && slide.endDate) {
      const current = dayjs(Date.now()).tz('America/New_York');
      const start = dayjs(slide.startDate).tz('America/New_York', true);
      const end = dayjs(slide.endDate).tz('America/New_York', true);
      return current.isAfter(start) && current.isBefore(end);
    }
    return true;
  });

const ImageSlider = ({ items, settings }) => {
  const slides = filterOutScheduledSlides(items);
  const [currentSlide, setCurrentSlide] = useState(slides?.[0]);

  const slideIntervalSeconds = settings?.autorotate_speed || 3;

  if (!slides?.length) {
    return <div>No slides</div>;
  }

  return (
    <Container
      boxVerticalAlignment={currentSlide.boxVerticalAlignment}
      boxHorizontalAlignment={currentSlide.boxHorizontalAlignment}
    >
      <ImageGallery
        items={slides}
        renderItem={shouldRenderAsLink}
        showThumbnails={false}
        showPlayButton={settings.play_button}
        showFullscreenButton={false}
        lazyLoad
        autoPlay={settings?.autorotate}
        slideInterval={slideIntervalSeconds * 1000}
        onBeforeSlide={nextIndex => setCurrentSlide(slides[nextIndex])}
      />
      {currentSlide &&
        (currentSlide.slide_title ||
          currentSlide.subheading ||
          currentSlide.button_label) && (
          <MobileTextOverlay
            textColor={currentSlide.textColor}
            boxColor={currentSlide.boxColor}
            boxTransparency={currentSlide.boxTransparency}
          >
            {currentSlide.slide_title && <h1>{currentSlide.slide_title}</h1>}
            {currentSlide.subheading && <p>{currentSlide.subheading}</p>}
            {currentSlide.button_label && currentSlide.link && (
              <Button onClick={() => navigate(currentSlide.link)}>
                {currentSlide.button_label}
              </Button>
            )}
          </MobileTextOverlay>
        )}
    </Container>
  );
};

ImageSlider.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  settings: PropTypes.shape({
    autorotate: PropTypes.bool,
    autorotate_speed: PropTypes.number,
    play_button: PropTypes.bool,
  }).isRequired,
};

export default ImageSlider;
